import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch } from '@mui/material';
import { useCustomers } from '../../hooks/useCustomers';
import CustomerSearchBar from '../shared/CustomerSearchBar';
import CustomerTable from './tables/CustomerTable';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorAlert from '../shared/ErrorAlert';
import { createCustomer, updateCustomer, getCustomer, deleteCustomer, restoreCustomer, getAllCustomersIncludingInactive } from '../../services/api/customerApi';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { validateCustomerForm } from '../../utils/customerValidation';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

const CustomerListView = () => {
  usePageBreadcrumbs([]);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('customers', 'manage');

  const {
    customers: activeCustomers,
    loading: activeLoading,
    error,
    searchTerm,
    setSearchTerm,
    refreshCustomers
  } = useCustomers();

  // Soft delete / restore state
  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allCustomersLoading, setAllCustomersLoading] = useState(false);

  // CRUD modal state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    customerCode: '',
    organizationDetails: '',
    isActive: true,
    augmentationEnabled: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch all customers (including inactive) when toggle is ON
  const fetchAllCustomers = useCallback(async () => {
    setAllCustomersLoading(true);
    try {
      const response = await getAllCustomersIncludingInactive({ pageNumber: 1, pageSize: 500 });
      const data = response.data;
      setAllCustomers(data.items || data || []);
    } catch (err) {
      showToast('Error loading customers', 'error');
    } finally {
      setAllCustomersLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (showInactive) {
      fetchAllCustomers();
    }
  }, [showInactive, fetchAllCustomers]);

  const customers = showInactive ? allCustomers : activeCustomers;
  const loading = showInactive ? allCustomersLoading : activeLoading;

  const handleManageCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleEditCustomer = async (customerId) => {
    setSelectedCustomerId(customerId);
    setFormLoading(true);
    setShowCustomerModal(true);
    try {
      const response = await getCustomer(customerId);
      const customer = response.data;
      setFormData({
        customerName: customer.customerName || '',
        customerCode: customer.customerCode || '',
        organizationDetails: customer.organizationDetails || '',
        isActive: customer.isActive ?? true,
        augmentationEnabled: customer.augmentationEnabled ?? false
      });
    } catch (err) {
      showToast('Error loading customer details', 'error');
      setShowCustomerModal(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddCustomerClick = () => {
    setSelectedCustomerId(null);
    setFormData({ customerName: '', customerCode: '', organizationDetails: '', isActive: true, augmentationEnabled: false });
    setValidationErrors({});
    setShowCustomerModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const handleFormSubmit = async () => {
    const validation = validateCustomerForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      showToast('Please fix validation errors before submitting', 'error');
      return;
    }
    setFormLoading(true);
    try {
      if (selectedCustomerId) {
        await updateCustomer(selectedCustomerId, formData);
        showToast('Customer updated successfully', 'success');
      } else {
        await createCustomer(formData);
        showToast('Customer created successfully', 'success');
      }
      setShowCustomerModal(false);
      setValidationErrors({});
      refreshCustomers();
    } catch (err) {
      showToast(selectedCustomerId ? 'Error updating customer' : 'Error creating customer', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowCustomerModal(false);
    setSelectedCustomerId(null);
    setValidationErrors({});
  };

  // Task 8.3: Delete confirmation handlers
  const handleDeleteClick = (id) => {
    setCustomerToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    try {
      await deleteCustomer(customerToDelete);
      showToast('Customer deleted successfully', 'success');
      refreshCustomers();
      if (showInactive) {
        fetchAllCustomers();
      }
    } catch (err) {
      showToast('Error deleting customer', 'error');
    } finally {
      setCustomerToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCustomerToDelete(null);
  };

  // Task 8.4: Restore handler
  const handleRestoreClick = async (id) => {
    try {
      await restoreCustomer(id);
      showToast('Customer restored successfully', 'success');
      refreshCustomers();
      fetchAllCustomers();
    } catch (err) {
      showToast('Error restoring customer', 'error');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Customer Management
          </Typography>
          {canManage && (
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label="Show inactive customers"
            />
          )}
        </Box>
        <Button variant="contained" color="primary" onClick={handleAddCustomerClick} aria-label="Add customer">
          Add Customer
        </Button>
        {canManage && (
          <Button variant="outlined" color="secondary" onClick={() => navigate('/customers/provision')} aria-label="Provision new customer" sx={{ ml: 1 }}>
            Provision New Customer
          </Button>
        )}
      </Box>

      {error && <ErrorAlert message={error} onClose={() => {}} />}

      <CustomerSearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      {loading && <LoadingSpinner size="large" />}

      {!loading && (
        <CustomerTable
          customers={customers}
          loading={loading}
          onManage={handleManageCustomer}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteClick}
          onRestore={handleRestoreClick}
          onAddRuleset={(customerId) => navigate(`/customers/provision?flowType=existing_customer_new_ruleset&customerId=${customerId}`)}
          showInactive={showInactive}
          canManage={canManage}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this customer? This action can be undone by restoring the customer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Customer CRUD Modal */}
      <Dialog open={showCustomerModal} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCustomerId ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Customer Name" value={formData.customerName} onChange={(e) => handleFormChange('customerName', e.target.value)} required fullWidth disabled={formLoading} error={!!validationErrors.customerName} helperText={validationErrors.customerName} />
            <TextField label="Customer Code" value={formData.customerCode} onChange={(e) => handleFormChange('customerCode', e.target.value)} fullWidth disabled={formLoading} error={!!validationErrors.customerCode} helperText={validationErrors.customerCode || 'Optional: Alphanumeric code with hyphens/underscores'} />
            <TextField label="Organization Details" value={formData.organizationDetails} onChange={(e) => handleFormChange('organizationDetails', e.target.value)} multiline rows={3} fullWidth disabled={formLoading} error={!!validationErrors.organizationDetails} helperText={validationErrors.organizationDetails} />
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => handleFormChange('isActive', e.target.checked)} disabled={formLoading} />} label="Active" />
            <FormControlLabel control={<Switch checked={formData.augmentationEnabled} onChange={(e) => handleFormChange('augmentationEnabled', e.target.checked)} disabled={formLoading} />} label="Augmentation Enabled" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} disabled={formLoading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={formLoading}>{selectedCustomerId ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomerListView;
